import {createSelector} from 'reselect'
import mapValues from 'lodash/mapValues.js'
import isEqual from 'lodash/isEqual.js'
import uniq from 'lodash/uniq.js'
import isEmpty from 'lodash/isEmpty.js'
import omitBy from 'lodash/omitBy.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
} from '../../store.js'
import {
  DEFAULT_SORT,
  DEFAULT_PER_PAGE,
  DEFAULT_EXPAND_MODE,
  STOCKTAKE_SORT_OPTIONS,
  STOCKTAKE_URI_COMPONENT,
  STOCKTAKE_TROOLEAN_FILTERS,
} from '../../common/constants/Stocktakes.js'
import {
  TAG_TYPE__STOCKTAKE,
  TAG_FILTER_OR,
  TAG_FILTER_OPTIONS,
} from '../../common/constants/Tags.js'
import {showEditTagModal} from '../../common/components/Modal/EditTagModal.js'
import apiverson from '../../common/apiverson.js'
import handleListSelection from '../../common/handleListSelection.js'
import {STOCKTAKE_LIST_PAGE} from '../../common/constants/Pages.js'
import {showGlobalError} from '../GlobalErrorMessage.js'
import {stocktakesSelector, setStocktakes} from '../../data/stocktakes.js'
import {navigate} from '../../common/location.js'
import {currentPageSelector} from '../../redux/selectors/ui/index.js'
import {locationSelector} from '../../redux/selectors/ui/location.js'
import {
  ensureArray,
  ensureArrayOfNumbers,
  ensureArrayOrUndefined,
  ensureBoolean,
  ensureTroolean,
} from '../../common/ensure.js'
import {isEmptyValue} from '../../common/utils.js'
import {
  EXPAND_MODE_EXPANDED,
  EXPAND_MODE_COLLAPSED,
} from '../../common/components/List/ExpandAllButton.js'
import {stocktakeTagsSortedByNameSelector} from '../../data/stocktakeTags.js'
import {debouncedSaveUserSettings, userSettingSelector} from '../../data/me.js'

export const STOCKTAKE_LIST_FORM = 'stocktake_list'
const STOCKTAKE__LIST__LIMIT = 'stocktake.list.limit'
const STOCKTAKE__LIST__SORT = 'stocktake.list.sort'
const STOCKTAKE__LIST__EXPAND_MODE = 'stocktake.list.expand_mode'

export function stocktakeListFormSelector(state) {
  return formsSelector(state)[STOCKTAKE_LIST_FORM]
}

export const referenceIDListSelector = createSelector(
  stocktakeListFormSelector,
  ({referenceIDList} = {}) => referenceIDList || [],
)

export function stocktakeListQuerySelector(state) {
  stocktakeListQuerySelector.cache = stocktakeListQuerySelector.cache || {}
  const cache = stocktakeListQuerySelector.cache

  const location = locationSelector(state)

  if (location === cache.location) {
    return cache.result
  }

  cache.location = location

  const result = {
    search: location.query.search || '',
    sort: location.query.sort || DEFAULT_SORT,
    limit: Number(location.query.limit) || DEFAULT_PER_PAGE,
    offset: Number(location.query.offset) || 0,
    started: ensureTroolean(location.query.started),
    completed: ensureTroolean(location.query.completed),
    cancelled: ensureTroolean(location.query.cancelled),
    warehouse_id: ensureArrayOfNumbers(location.query.warehouse_id),
    tag: ensureArray(location.query.tag),
    exclude_tags: ensureArray(location.query.exclude_tags),
    untagged: ensureBoolean(location.query.untagged),
    tag_filter_by: location.query.tag_filter_by || TAG_FILTER_OR,
  }

  if (isEqual(cache.result, result)) {
    return cache.result
  }

  cache.result = result

  return result
}

export const isLoadingSelector = createSelector(
  stocktakeListFormSelector,
  ({isLoading} = {}) => isLoading || false,
)

export const countSelector = createSelector(
  stocktakeListFormSelector,
  ({count} = {}) => count || 0,
)

export function expandModeSelector(state) {
  return userSettingSelector(state, {
    setting: STOCKTAKE__LIST__EXPAND_MODE,
    defaultValue: DEFAULT_EXPAND_MODE,
  })
}

export const expandedReferenceIDsSelector = createSelector(
  stocktakeListFormSelector,
  ({expandedReferenceIDs} = {}) => expandedReferenceIDs || [],
)

export const selectedReferenceIDsSelector = createSelector(
  stocktakeListFormSelector,
  ({selectedReferenceIDs} = {}) => selectedReferenceIDs || [],
)

export const allSelectedSelector = createSelector(
  referenceIDListSelector,
  selectedReferenceIDsSelector,
  (referenceIDList, selected) =>
    referenceIDList.length > 0 && referenceIDList.length === selected.length,
)

export const indeterminateSelectedSelector = createSelector(
  allSelectedSelector,
  selectedReferenceIDsSelector,
  (allSelected, selectedReferenceIDs) =>
    !allSelected && selectedReferenceIDs.length > 0,
)

export const firstSelectedReferenceIDSelector = createSelector(
  selectedReferenceIDsSelector,
  (selectedReferenceIDs) => selectedReferenceIDs[0],
)

export const showStocktakeStatusBadge = createSelector(
  stocktakeListQuerySelector,
  (query) =>
    [query.started, query.completed, query.cancelled].reduce(
      (prev, hasStatus) => prev + (hasStatus ? 1 : 0),
      0,
    ) !== 1,
)

export function navigateToStocktakeListPage(query) {
  const defaultQuery = defaultHashParamsSelector(getState())

  return navigate([STOCKTAKE_URI_COMPONENT], {...defaultQuery, ...query})
}

export function defaultHashParamsSelector(state) {
  const limit = userSettingSelector(state, {
    setting: STOCKTAKE__LIST__LIMIT,
    defaultValue: DEFAULT_PER_PAGE,
  })
  const sort = userSettingSelector(state, {
    setting: STOCKTAKE__LIST__SORT,
    defaultValue: DEFAULT_SORT,
  })

  return {
    completed: false,
    cancelled: false,
    limit: limit !== DEFAULT_PER_PAGE ? limit : undefined,
    sort: sort !== DEFAULT_SORT ? sort : undefined,
  }
}

export function queryParamsSelector(state) {
  const {
    search,
    sort,
    started,
    completed,
    cancelled,
    warehouse_id,
    tag,
    exclude_tags,
    untagged,
    tag_filter_by,
    limit,
    offset,
  } = stocktakeListQuerySelector(state)

  return {
    search: search.trim() || undefined,
    started: ensureTroolean(started),
    completed: ensureTroolean(completed),
    cancelled: ensureTroolean(cancelled),
    warehouse_id: warehouse_id.length ? warehouse_id : undefined,
    sort,
    tag: tag.length ? tag : undefined,
    exclude_tags: exclude_tags.length ? exclude_tags : undefined,
    untagged: untagged || undefined,
    tag_filter_by: tag_filter_by !== TAG_FILTER_OR ? tag_filter_by : undefined,
    limit,
    offset,
  }
}

export function cleanStocktakeQuery(query) {
  return Object.entries(query)
    .filter(([key, value]) => {
      if (
        ![
          'limit',
          'offset',
          'started',
          'completed',
          'cancelled',
          'warehouse_id',
          'tag',
          'exclude_tags',
          'tag_filter_by',
          'untagged',
          'search',
          'sort',
        ].includes(key)
      ) {
        return false
      }

      if (key === 'untagged' && !value) {
        return false
      }

      if (Array.isArray(value) ? value.length === 0 : isEmptyValue(value)) {
        return false
      }

      return true
    })
    .reduce((prev, [key, value]) => {
      prev[key] = value
      return prev
    }, {})
}

export const createIsSelectedSelector = (referenceID) =>
  createSelector(selectedReferenceIDsSelector, (selectedReferenceIDs) => {
    return selectedReferenceIDs.includes(referenceID)
  })

export const createIsExpandedSelector = (referenceID) =>
  createSelector(
    expandedReferenceIDsSelector,
    expandModeSelector,
    (expandedReferenceIDs, expandMode) => {
      if (expandMode === EXPAND_MODE_COLLAPSED) {
        return expandedReferenceIDs.includes(referenceID)
      }
      if (expandMode === EXPAND_MODE_EXPANDED) {
        return !expandedReferenceIDs.includes(referenceID)
      }
      return false
    },
  )

export const isFilteredSelector = createSelector(
  queryParamsSelector,
  (queryParams) =>
    !isEmpty(
      omitBy(
        queryParams,
        (value, key) =>
          ['sort', 'limit', 'offset'].includes(key) ||
          (key !== 'started' && isEmptyValue(value)) ||
          (key === 'started' && !value),
      ),
    ),
)

export function createStocktakeTagQuantityMapSelector() {
  return createSelector(
    (state, {referenceIDs}) => referenceIDs,
    stocktakesSelector,
    stocktakeTagsSortedByNameSelector,
    (referenceIDs, stocktakes, stocktakeTags) => {
      const stocktakeTagQuantityMap = stocktakeTags.reduce(
        (prev, tag) => ({
          ...prev,
          [tag.id]: 0,
        }),
        {},
      )

      referenceIDs.forEach((referenceID) => {
        const stocktake = stocktakes[referenceID]

        if (!stocktake) {
          return
        }

        stocktake.tags.forEach((tag) => {
          stocktakeTagQuantityMap[tag.id] += 1
        })
      })

      return mapValues(stocktakeTagQuantityMap, (selectedQuantity) => {
        const diff = referenceIDs.length - selectedQuantity
        if (diff === 0) {
          return 'all'
        } else if (diff === referenceIDs.length) {
          return 'none'
        }

        return 'some'
      })
    },
  )
}

export function setupStocktakeListForm() {
  setForm(STOCKTAKE_LIST_FORM, {
    isLoading: false,
    count: 0,
    referenceIDList: [],
    selectedReferenceIDs: [],
    expandedReferenceIDs: [],
  })
}

export function updateStocktakeListForm(updates, meta) {
  updateForm(STOCKTAKE_LIST_FORM, updates, meta)
}

export function removeStocktakeListForm() {
  removeForm(STOCKTAKE_LIST_FORM)
}

export function navigateStocktakeList(updates = {}) {
  let {pathComponents, query} = locationSelector(getState())

  if (pathComponents[0] !== STOCKTAKE_URI_COMPONENT) {
    return
  } else {
    query = {...query, ...updates}
  }

  return navigate(pathComponents, query)
}

export function resetOffset() {
  return {
    offset: undefined,
  }
}

export function updateLimit(limit) {
  limit = Number(limit)

  if (![10, 50, 100].includes(limit)) {
    limit = DEFAULT_PER_PAGE
  }

  if (limit === DEFAULT_PER_PAGE) {
    limit = undefined
  }

  return {
    limit,
    ...resetOffset(),
  }
}

export function setLimit(limit) {
  const updates = updateLimit(limit)

  debouncedSaveUserSettings({[STOCKTAKE__LIST__LIMIT]: updates.limit})

  return navigateStocktakeList(updates)
}

export function updateSort(sort) {
  if (!STOCKTAKE_SORT_OPTIONS.find(({value}) => sort === value)) {
    sort = DEFAULT_SORT
  }

  if (sort === DEFAULT_SORT) {
    sort = undefined
  }

  return {
    sort,
    ...resetOffset(),
  }
}

export function setSort(sort) {
  const updates = updateSort(sort)

  debouncedSaveUserSettings({[STOCKTAKE__LIST__SORT]: updates.sort})

  return navigateStocktakeList(updates)
}

export function updateSearchText(search) {
  search = (search || '').trim() || undefined

  return {
    search,
    ...resetOffset(),
  }
}

export function setSearchText(search) {
  return navigateStocktakeList(updateSearchText(search))
}

export function updateStocktakeTroolean(updates) {
  updates = Object.entries(updates).reduce((prev, [key, value]) => {
    if (STOCKTAKE_TROOLEAN_FILTERS.includes(key)) {
      prev[key] = value === true ? true : value === false ? false : undefined
    }

    return prev
  }, {})

  return {
    ...updates,
    ...resetOffset(),
  }
}

export function setStocktakeTroolean(updates) {
  return navigateStocktakeList(updateStocktakeTroolean(updates))
}

export function updateStocktakeWarehouseIDs(warehouseIDs) {
  return {
    warehouse_id: ensureArrayOrUndefined(warehouseIDs),
    ...resetOffset(),
  }
}

export function setStocktakeWarehouseIDs(warehouseIDs) {
  return navigateStocktakeList(updateStocktakeWarehouseIDs(warehouseIDs))
}

export function removeStocktakeWarehouseID(warehouseID) {
  const {warehouse_id} = stocktakeListQuerySelector(getState())

  return setStocktakeWarehouseIDs(
    warehouse_id.filter((id) => id !== warehouseID),
  )
}

export function updateStocktakeTags(tag) {
  tag = tag || []

  if (!Array.isArray(tag)) {
    tag = [tag]
  }

  if (tag.length === 0) {
    tag = undefined
  } else {
    tag = uniq(tag)
  }

  return {
    tag,
    ...resetOffset(),
  }
}

export function setStocktakeTags(tags) {
  return navigateStocktakeList(updateStocktakeTags(tags))
}

export function removeStocktakeTagFilter(tagName) {
  const {tag} = stocktakeListQuerySelector(getState())

  return setStocktakeTags(tag.filter((name) => name !== tagName))
}

export function updateStocktakeExcludeTags(exclude_tags) {
  return {
    exclude_tags: uniq(ensureArrayOrUndefined(exclude_tags)),
    ...resetOffset(),
  }
}

export function setStocktakeExcludeTags(exclude_tags) {
  return navigateStocktakeList(updateStocktakeExcludeTags(exclude_tags))
}

export function removeStocktakeExcludeTag(tagName) {
  const {exclude_tags} = stocktakeListQuerySelector(getState())

  return setStocktakeExcludeTags(
    exclude_tags.filter((name) => name !== tagName),
  )
}

export function updateStocktakeUntagged(untagged) {
  if (!untagged) {
    untagged = undefined
  }

  return {
    untagged,
    ...resetOffset(),
  }
}

export function setStocktakeUntagged(untagged) {
  return navigateStocktakeList(updateStocktakeUntagged(untagged))
}

export function updateStocktakeTagFilterBy(tag_filter_by) {
  if (!TAG_FILTER_OPTIONS.find(({value}) => tag_filter_by === value)) {
    tag_filter_by = TAG_FILTER_OR
  }

  if (tag_filter_by === TAG_FILTER_OR) {
    tag_filter_by = undefined
  }

  return {
    tag_filter_by,
    ...resetOffset(),
  }
}

export function setStocktakeTagFilterBy(tag_filter_by) {
  return navigateStocktakeList(updateStocktakeTagFilterBy(tag_filter_by))
}

export function updateOffset(offset) {
  offset = Number(offset) || 0

  if (!offset) {
    offset = undefined
  }

  return {
    offset,
  }
}

export function setOffset(offset) {
  return navigateStocktakeList(updateOffset(offset))
}

export function setExpandedReferenceIDs(expandedReferenceIDs) {
  updateStocktakeListForm({
    expandedReferenceIDs,
  })
}

export function setExpandMode(expandMode) {
  debouncedSaveUserSettings({[STOCKTAKE__LIST__EXPAND_MODE]: expandMode})
}

export function setSelectedReferenceIDs(selectedReferenceIDs) {
  updateStocktakeListForm({
    selectedReferenceIDs,
  })
}

export function createAndApplyTagToStocktakes(referenceIDs) {
  showEditTagModal(TAG_TYPE__STOCKTAKE, null, {
    addTagToStocktakeReferenceIDs: referenceIDs,
  })
}

export async function refreshStocktakeList() {
  try {
    const currentPage = currentPageSelector(getState())
    if (currentPage !== STOCKTAKE_LIST_PAGE) {
      return
    }

    updateStocktakeListForm({isLoading: true})

    const params = queryParamsSelector(getState())

    const {json} = await apiverson.get('/stocktake/', params)

    setStocktakes(json.stocktake)

    updateQueryResults(json)
  } catch (err) {
    showGlobalError(
      {
        summary: 'Error querying Stocktakes.',
        details: err.message || err.error_message,
      },
      err,
    )
  }

  updateStocktakeListForm({isLoading: false})
}

export function updateQueryResults({count, stocktake}) {
  const {selectedReferenceIDs, expandedReferenceIDs} =
    stocktakeListFormSelector(getState())
  const referenceIDList = stocktake.map((stocktake) => stocktake.reference_id)

  updateStocktakeListForm({
    referenceIDList,
    count,
    selectedReferenceIDs: selectedReferenceIDs.filter((referenceID) =>
      referenceIDList.includes(referenceID),
    ),
    expandedReferenceIDs: expandedReferenceIDs.filter((referenceID) =>
      referenceIDList.includes(referenceID),
    ),
  })
}

export async function stocktakeListSelectReferenceID(
  referenceID,
  isSelected,
  isShiftKey,
) {
  const selectedReferenceIDs = selectedReferenceIDsSelector(getState())
  const referenceIDList = referenceIDListSelector(getState())

  setSelectedReferenceIDs(
    handleListSelection({
      value: referenceID,
      isSelected,
      isShiftKey,
      selected: selectedReferenceIDs,
      list: referenceIDList,
    }),
  )
}

export async function deselectStocktakes(referenceIDs) {
  const selectedReferenceIDs = selectedReferenceIDsSelector(getState())

  setSelectedReferenceIDs(
    selectedReferenceIDs.filter((id) => !referenceIDs.includes(id)),
  )
}

export async function stocktakeListToggleExpanded(referenceID) {
  const expandedReferenceIDs = expandedReferenceIDsSelector(getState())

  const referenceIDs = expandedReferenceIDs.filter((p) => p !== referenceID)

  if (!expandedReferenceIDs.includes(referenceID)) {
    referenceIDs.push(referenceID)
  }

  setExpandedReferenceIDs(referenceIDs)
}
